/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:06]
Permissions to update outside the main branch: [false]
*/

import { ToastProviderState } from '@finbeat/react-toolkit';
import { AxiosError, AxiosResponse } from 'axios';
import { t } from 'i18next';
import UploadResponse from 'models/api/uploadResponse';

type UploadCallbackType = (
    file: File,
    onUploadProgress: (e: ProgressEvent) => void
) => Promise<AxiosResponse<UploadResponse, any>>;

type FileStats = { percentage: number; fileName: string };

export type UploadResult = 'warning' | 'success' | 'error' | 'unknown';

export class FileUploadNotificator {
    _currentToastId?: string;
    _upload: UploadCallbackType;
    _toasts: ToastProviderState;
    _callback: ((res: UploadResult, additionalInfo: any) => void) | undefined;

    constructor(
        upload: UploadCallbackType,
        toasts: ToastProviderState,
        callback?: (res: UploadResult, additionalInfo: any) => void
    ) {
        this._upload = upload;
        this._toasts = toasts;
        this._callback = callback;
    }

    async uploadFiles(selectedFiles?: FileList) {
        if (selectedFiles) {
            const files = Array.from(selectedFiles);
            if (files.length) {
                const propress = files.map((file) => ({
                    percentage: 0,
                    fileName: file.name,
                }));
                this.setProgressInfos(propress);
                await Promise.all(files.map((file, i) => this.uploadAction(i, file, propress)));
            }
        }
    }

    uploadAction(idx: number, file: File, fileStats: FileStats[]) {
        const fileName = fileStats[idx].fileName;
        this.setProgressInfos([...fileStats]);
        return this._upload(file, (event) => {
            const progress = Math.round((100 * event.loaded) / event.total);
            fileStats[idx].percentage = progress;
            this.setProgressInfos([...fileStats]);
        })
            .then((res) => {
                if (this._currentToastId) {
                    this._toasts.removeToast(this._currentToastId);
                }

                if (res.data.type.toLowerCase() === 'warning') {
                    this._toasts.addToast({
                        title: t('BackOfficeToastHeaderWarning'),
                        autoDeleteTime: null,
                        type: 'warning',
                        description: t('BackOfficeFileUploadingWarningMessage').replace(
                            '{0}',
                            fileName
                        ),
                        data: res.data.errRecs,
                    });
                    this._callback?.('warning', res.data?.additionalInfo);
                } else if (res.data.type.toLowerCase() === 'success') {
                    this._toasts.addToast({
                        title: t('BackOfficeToastHeaderSuccess'),
                        type: 'success',
                        description: t('BackOfficeFileUploadingSuccessMessage').replace(
                            '{0}',
                            fileName
                        ),
                    });
                    this._callback?.('success', res.data?.additionalInfo);
                }
            })
            .catch(({ response }: AxiosError<any>) => {
                if (this._currentToastId) {
                    this._toasts.removeToast(this._currentToastId);
                }

                if (response?.data?.type?.toLowerCase() === 'error') {
                    this._toasts.addToast({
                        title: t('BackOfficeToastHeaderError'),
                        type: 'danger',
                        autoDeleteTime: null,
                        description: (response.data as UploadResponse).message,
                    });
                    this._callback?.('error', null);
                } else if (response?.data?.errorCode != null) {
                    this._toasts.addToast({
                        title: t('BackOfficeToastHeaderError'),
                        type: 'danger',
                        autoDeleteTime: null,
                        description:
                            response?.data?.errorCode !== 'BackOfficeOopsSomethingWentWrong'
                                ? t(response?.data?.errorCode)
                                : t('BackOfficeFileUploadingUnknownErrorMessage').replace(
                                      '{0}',
                                      fileName
                                  ),
                    });
                    this._callback?.('unknown', null);
                } else {
                    this._toasts.addToast({
                        title: t('BackOfficeToastHeaderError'),
                        type: 'danger',
                        autoDeleteTime: null,
                        description: t('BackOfficeFileUploadingUnknownErrorMessage').replace(
                            '{0}',
                            fileName
                        ),
                    });
                    this._callback?.('unknown', null);
                }
            });
    }

    setProgressInfos(progressInfos: FileStats[] | null) {
        if (progressInfos?.length) {
            if (!this._currentToastId) {
                this._currentToastId = this._toasts.addToast({
                    title: t('BackOfficeToastHeaderUploading'),
                    type: 'info',
                    description: t('BackOfficeFileUploadingProgressMessage').replace(
                        '{0}',
                        progressInfos[0].fileName
                    ),
                    progress: progressInfos[0].percentage,
                    autoDeleteTime: null,
                });
            } else {
                this._toasts.updateToast({
                    id: this._currentToastId,
                    progress: progressInfos[0].percentage,
                });
            }
        }
    }
}
